import { adminFetch } from '../../lib/adminFetch';
import React, { useState, useEffect } from 'react';

// メールプロバイダ別の接続先プリセット（選ぶとホスト・ポートを自動入力）
const MAIL_PROVIDERS = {
  xserver: { label: 'XServer（@srwith.com など）', imapHost: 'sv12128.xserver.jp', imapPort: 993, smtpHost: 'sv12128.xserver.jp', smtpPort: 587 },
  gmail: { label: 'Gmail（要アプリパスワード）', imapHost: 'imap.gmail.com', imapPort: 993, smtpHost: 'smtp.gmail.com', smtpPort: 587 },
  custom: { label: 'その他（手動設定）', imapHost: '', imapPort: 993, smtpHost: '', smtpPort: 587 },
};

const emptyAccountForm = () => ({
  key: '', email: '', label: '', provider: 'xserver',
  imapHost: MAIL_PROVIDERS.xserver.imapHost, imapPort: 993,
  smtpHost: MAIL_PROVIDERS.xserver.smtpHost, smtpPort: 587,
  user: '', pass: '', signature: '',
});

const MailAdmin = () => {
  const [inquiries, setInquiries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyFromKey, setReplyFromKey] = useState(''); // 空＝表示中のアカウントのまま送信
  const [sending, setSending] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [message, setMessage] = useState('');
  const [templates, setTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState({ title: '', body: '' });
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [listOpen, setListOpen] = useState(true);
  const [visibleCount, setVisibleCount] = useState(10);
  const [attachments, setAttachments] = useState([]);
  const [search, setSearch] = useState('');
  const [showBlocked, setShowBlocked] = useState(false); // 配信停止フォルダ表示
  // メールアカウント（共有＋自分の個人箱）
  const [accounts, setAccounts] = useState([]);
  const [showAccounts, setShowAccounts] = useState(false);
  const [accountForm, setAccountForm] = useState(emptyAccountForm());
  const [accountMsg, setAccountMsg] = useState('');
  const [savingAccount, setSavingAccount] = useState(false);
  const [account, setAccount] = useState('infoweb'); // 選択中のメールアカウント
  const [mlBlacklist, setMlBlacklist] = useState([]); // ML配信停止（ブラックリスト）

  useEffect(() => {
    adminFetch('/api/admin-read.php?file=templates&t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setTemplates)
      .catch(() => setTemplates([]));
    adminFetch('/api/admin-read.php?file=ml-blacklist&t=' + Date.now())
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setMlBlacklist(Array.isArray(d) ? d : []))
      .catch(() => setMlBlacklist([]));
    loadAccounts();
  }, []);

  // アカウントタブを切り替えたら返信元の上書き選択はリセット（別アカウントのつもりで送るのを防ぐ）
  useEffect(() => { setReplyFromKey(''); }, [account]);

  const isBlacklisted = (email) => !!email && mlBlacklist.some((b) => (b || '').toLowerCase() === email.toLowerCase());

  // ML配信停止（ブラックリスト）に追加/解除
  const toggleBlacklistEmail = async (email) => {
    if (!email) return;
    const el = email.toLowerCase();
    const inBl = mlBlacklist.some((b) => (b || '').toLowerCase() === el);
    const next = inBl ? mlBlacklist.filter((b) => (b || '').toLowerCase() !== el) : [...mlBlacklist, email];
    setMlBlacklist(next);
    try {
      await adminFetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'ml-blacklist', data: next }),
      });
      setMessage(inBl ? '配信停止(BL)から外しました' : '配信停止(BL)に追加しました');
    } catch {
      setMlBlacklist(mlBlacklist);
      setMessage('配信停止リストの更新に失敗しました');
    }
  };

  // アカウント切り替え時に、そのアカウントの受信箱を読み込む
  useEffect(() => {
    loadInquiries(account);
    setSelected(null);
    setSearch('');
    setVisibleCount(10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const loadInquiries = (key) => {
    adminFetch('/api/admin-mail.php?action=list&key=' + encodeURIComponent(key))
      .then((r) => (r.ok ? r.json() : { inquiries: [] }))
      .then((data) => setInquiries(data.inquiries || []))
      .catch(() => setInquiries([]));
  };

  const loadAccounts = () => {
    adminFetch('/api/admin-mail-accounts.php?action=list')
      .then((r) => (r.ok ? r.json() : { accounts: [] }))
      .then((data) => setAccounts(data.accounts || []))
      .catch(() => setAccounts([]));
  };

  // プロバイダ選択でホスト・ポートを自動入力
  const pickProvider = (provider) => {
    const p = MAIL_PROVIDERS[provider] || MAIL_PROVIDERS.custom;
    setAccountForm((f) => ({
      ...f, provider,
      imapHost: p.imapHost, imapPort: p.imapPort, smtpHost: p.smtpHost, smtpPort: p.smtpPort,
    }));
  };

  const openAddAccount = () => {
    setAccountForm(emptyAccountForm());
    setAccountMsg('');
    setShowAccounts(true);
  };

  const editAccount = (a) => {
    // プロバイダ推定（ホストで判定）
    const provider = a.imapHost === MAIL_PROVIDERS.gmail.imapHost ? 'gmail'
      : a.imapHost === MAIL_PROVIDERS.xserver.imapHost ? 'xserver' : 'custom';
    setAccountForm({
      key: a.key, email: a.email || '', label: a.label || '', provider,
      imapHost: a.imapHost || '', imapPort: a.imapPort || 993,
      smtpHost: a.smtpHost || '', smtpPort: a.smtpPort || 587,
      user: a.user || '', pass: '', signature: a.signature || '',
    });
    setAccountMsg('');
  };

  const saveAccount = async () => {
    if (!accountForm.email.trim()) { setAccountMsg('メールアドレスを入力してください'); return; }
    if (!accountForm.key && !accountForm.pass.trim()) { setAccountMsg('パスワードを入力してください'); return; }
    setSavingAccount(true);
    setAccountMsg('');
    try {
      const isEdit = !!accountForm.key;
      const res = await adminFetch(`/api/admin-mail-accounts.php?action=${isEdit ? 'update' : 'add'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(accountForm),
      });
      const data = await res.json();
      if (data.success) {
        setAccountMsg(isEdit ? '更新しました' : '追加しました');
        setAccountForm(emptyAccountForm());
        loadAccounts();
      } else {
        setAccountMsg(data.message || '保存に失敗しました');
      }
    } catch {
      setAccountMsg('保存に失敗しました');
    } finally {
      setSavingAccount(false);
    }
  };

  const deleteAccount = async (a) => {
    if (!window.confirm(`アカウント「${a.label || a.email}」を削除しますか？`)) return;
    try {
      const res = await adminFetch('/api/admin-mail-accounts.php?action=delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: a.key }),
      });
      const data = await res.json();
      if (data.success) { loadAccounts(); setAccountMsg('削除しました'); }
      else setAccountMsg(data.message || '削除に失敗しました');
    } catch {
      setAccountMsg('削除に失敗しました');
    }
  };

  const fetchMail = async () => {
    setFetching(true);
    setMessage('');
    try {
      const res = await adminFetch('/api/admin-mail.php?action=fetch&key=' + encodeURIComponent(account));
      const data = await res.json();
      if (data.success) {
        setMessage(data.newCount > 0 ? `${data.newCount}件の新着メールを取り込みました` : '新着メールはありません');
        // 一覧を再取得
        const listRes = await adminFetch('/api/admin-mail.php?action=list&key=' + encodeURIComponent(account));
        const listData = await listRes.json();
        setInquiries(listData.inquiries || []);
      } else {
        setMessage('受信チェックに失敗しました: ' + (data.message || ''));
      }
    } catch {
      setMessage('受信チェックに失敗しました');
    } finally {
      setFetching(false);
    }
  };

  const saveTemplate = async () => {
    if (!newTemplate.title.trim() || !newTemplate.body.trim()) return;
    const updated = [...templates, { id: Date.now(), ...newTemplate }];
    try {
      await adminFetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'templates', data: updated }),
      });
      setTemplates(updated);
      setNewTemplate({ title: '', body: '' });
      setShowTemplateForm(false);
    } catch {}
  };

  const deleteTemplate = async (id) => {
    const updated = templates.filter((t) => t.id !== id);
    try {
      await adminFetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'templates', data: updated }),
      });
      setTemplates(updated);
    } catch {}
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('このメールを削除しますか？')) return;
    try {
      await adminFetch('/api/admin-mail.php?action=delete&key=' + encodeURIComponent(account), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      setInquiries((prev) => prev.filter((item) => item.id !== id));
      if (selected?.id === id) setSelected(null);
      setMessage('削除しました');
    } catch {
      setMessage('削除に失敗しました');
    }
  };

  const markSpam = async (id) => {
    if (!window.confirm('このメールを迷惑メールにして、今後この差出人をブロックしますか？')) return;
    try {
      await adminFetch('/api/admin-mail.php?action=spam&key=' + encodeURIComponent(account), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      setInquiries((prev) => prev.filter((item) => item.id !== id));
      if (selected?.id === id) setSelected(null);
      setMessage('迷惑メールとして処理しました');
    } catch {
      setMessage('迷惑メール処理に失敗しました');
    }
  };

  // CW通知のオン/オフ切替（基本オン。オフにするとそのスレッドは以後通知しない）
  const toggleChatwork = async (item) => {
    const cur = item.chatworkOn !== false; // 未設定はオン扱い
    const next = !cur;
    const apply = (val) => {
      setInquiries((prev) => prev.map((m) => (m.id === item.id ? { ...m, chatworkOn: val } : m)));
      setSelected((prev) => (prev && prev.id === item.id ? { ...prev, chatworkOn: val } : prev));
    };
    apply(next); // 楽観的更新
    try {
      const res = await adminFetch('/api/admin-mail.php?action=chatworkToggle&key=' + encodeURIComponent(account), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, on: next }),
      });
      const data = await res.json();
      if (data.success) {
        apply(data.chatworkOn);
        setMessage(data.chatworkOn ? 'ChatWork配信OKにしました' : 'ChatWork配信しない（除外）にしました');
      } else {
        apply(cur);
        setMessage('CW通知の切り替えに失敗しました');
      }
    } catch {
      apply(cur);
      setMessage('CW通知の切り替えに失敗しました');
    }
  };

  const [scanning, setScanning] = useState(false);
  const bounceCleanup = async () => {
    if (!window.confirm('この受信箱のバウンス（配信失敗）＋仮登録/本登録などの自動返信を解析し、対象アドレスを配信停止(BL)に追加します。よろしいですか？')) return;
    setScanning(true);
    setMessage('バウンス・自動返信を解析中...（少し時間がかかります）');
    try {
      const res = await adminFetch('/api/admin-mail.php?action=bounceScan&key=' + encodeURIComponent(account));
      const data = await res.json();
      if (data.success) {
        setMessage(`整理完了：バウンス${data.bounces}件・自動返信${data.autoreplies || 0}件 → 対象${data.failedFound}件検出 → ${data.added}件を新規に配信停止(BL)へ追加（BL合計${data.blacklistTotal}件）`);
      } else {
        setMessage('バウンス整理に失敗しました: ' + (data.message || ''));
      }
    } catch {
      setMessage('バウンス整理に失敗しました');
    } finally {
      setScanning(false);
    }
  };

  const markUnread = async (id) => {
    try {
      await adminFetch('/api/admin-mail.php?action=markUnread&key=' + encodeURIComponent(account), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, read: false } : i)));
      setSelected((prev) => (prev && prev.id === id ? { ...prev, read: false } : prev));
      setMessage('未読に戻しました');
    } catch {
      setMessage('未読に戻すのに失敗しました');
    }
  };

  const openDetail = (item) => {
    setSelected(item);
    setReplyText('');
    setAttachments([]);
    setMessage('');
    if (!item.read) {
      adminFetch('/api/admin-mail.php?action=markRead&key=' + encodeURIComponent(account), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id }),
      }).catch(() => {});
      setInquiries((prev) => prev.map((i) => (i.id === item.id ? { ...i, read: true } : i)));
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('to', selected.email);
      formData.append('name', selected.name);
      formData.append('subject', 'Re: 【SrWith】お問い合わせについて');
      formData.append('body', replyText);
      formData.append('inquiryId', selected.id);
      if (replyFromKey) formData.append('fromKey', replyFromKey);
      attachments.forEach((file) => formData.append('attachments[]', file));

      const res = await adminFetch('/api/admin-mail.php?action=reply&key=' + encodeURIComponent(account), {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setMessage('返信しました');
        const fileNames = attachments.map((f) => f.name);
        const newReply = { date: new Date().toISOString(), body: replyText, attachments: fileNames };
        setReplyText('');
        setAttachments([]);
        setInquiries((prev) =>
          prev.map((item) =>
            item.id === selected.id
              ? { ...item, replied: true, replies: [...(item.replies || []), newReply] }
              : item
          )
        );
        setSelected((prev) => ({
          ...prev,
          replied: true,
          replies: [...(prev.replies || []), newReply],
        }));
      } else {
        setMessage('送信に失敗しました: ' + (data.message || ''));
      }
    } catch {
      setMessage('送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ja-JP') + ' ' + d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  };

  // 検索（名前・メール・種別・本文・返信内容を部分一致・大文字小文字問わず）
  const q = search.trim().toLowerCase();
  const blSet = new Set(mlBlacklist.map((b) => (b || '').toLowerCase()));
  const blockedCount = inquiries.filter((it) => blSet.has((it.email || '').toLowerCase())).length;
  const matchesSearch = (item) => {
    if (q === '') return true;
    const fields = [item.name, item.email, item.purposeLabel, item.type, item.details];
    if (fields.some((f) => (f || '').toLowerCase().includes(q))) return true;
    return (item.replies || []).some((r) => (r.body || '').toLowerCase().includes(q));
  };
  // 通常は配信停止(BL)の差出人を隠す。「配信停止フォルダ」表示中は配信停止のみ表示。
  const filtered = inquiries.filter((item) => {
    const isBl = blSet.has((item.email || '').toLowerCase());
    if (showBlocked ? !isBl : isBl) return false;
    return matchesSearch(item);
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold">メール管理</h2>
          <div className="flex items-center gap-3 text-xs text-gray-600">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded-full" />新規</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full" />既読</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-400 rounded-full" />返信済</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openAddAccount}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-50"
          >
            メールアカウント
          </button>
          {account !== 'infoweb' && (
            <button
              onClick={bounceCleanup}
              disabled={scanning}
              title="この受信箱のバウンスを解析して失敗アドレスを配信停止(BL)に追加"
              className="bg-white border border-orange-400 text-orange-600 px-4 py-2 rounded-lg text-sm font-bold hover:bg-orange-50 disabled:opacity-50"
            >
              {scanning ? '解析中...' : 'バウンス整理'}
            </button>
          )}
          <button
            onClick={fetchMail}
            disabled={fetching}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
          >
            {fetching ? 'チェック中...' : 'メール受信チェック'}
          </button>
        </div>
      </div>

      {/* アカウントタブ（共有＋自分の個人箱） */}
      {accounts.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {accounts.map((a) => (
            <button
              key={a.key}
              onClick={() => setAccount(a.key)}
              title={a.email}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap border transition-colors ${
                account === a.key
                  ? 'bg-pink-dark border-pink-dark text-white shadow'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-pink-beige hover:text-pink-dark'
              }`}
            >
              {a.label || a.email}
              {a.shared && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  account === a.key ? 'bg-white/20 text-white' : 'bg-pink-beige/40 text-pink-dark'
                }`}>共有</span>
              )}
            </button>
          ))}
        </div>
      )}

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      <div className="flex gap-4">
        {/* 一覧 */}
        <div className={`shrink-0 transition-all duration-300 ${listOpen ? 'w-[20%]' : 'w-10'}`}>
          {listOpen ? (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
                <span className="text-xs font-bold text-gray-500">
                  {showBlocked
                    ? `配信停止 ${filtered.length}件`
                    : `${q ? `${filtered.length}/${inquiries.length - blockedCount}` : inquiries.length - blockedCount}件`}
                </span>
                <button onClick={() => setListOpen(false)} className="text-gray-400 hover:text-gray-600 text-sm">&#x2039;</button>
              </div>
              {blockedCount > 0 && (
                <button
                  onClick={() => { setShowBlocked((v) => !v); setVisibleCount(10); setSelected(null); }}
                  className={`w-full text-left px-3 py-1.5 text-[11px] font-bold border-b ${showBlocked ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-white text-red-600 hover:bg-red-50'}`}
                >
                  {showBlocked ? '← 通常の受信箱に戻る' : `🚫 配信停止フォルダ（${blockedCount}）`}
                </button>
              )}
              {inquiries.length > 0 && (
                <div className="px-2 py-2 border-b">
                  <div className="relative">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setVisibleCount(10); }}
                      placeholder="名前・メール・本文で検索"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 pr-6 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {search && (
                      <button
                        onClick={() => { setSearch(''); setVisibleCount(10); }}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm font-bold"
                        title="クリア"
                      >×</button>
                    )}
                  </div>
                </div>
              )}
              {inquiries.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-xs">なし</div>
              ) : filtered.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-xs">該当するメールがありません</div>
              ) : (
                <>
                  <div className="divide-y max-h-[70vh] overflow-y-auto">
                    {filtered.slice(0, visibleCount).map((item) => (
                      <button
                        key={item.id}
                        onClick={() => openDetail(item)}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors ${
                          selected?.id === item.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-1 mb-0.5">
                          <span
                            className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                              !item.read
                                ? 'bg-blue-500'
                                : item.replied
                                ? 'bg-yellow-400'
                                : 'bg-red-500'
                            }`}
                          />
                          <span className="text-[10px] text-gray-400 truncate">{formatDate(item.date)}</span>
                        </div>
                        <p className="font-bold text-xs truncate">
                          {isBlacklisted(item.email) && <span className="text-[9px] bg-red-600 text-white rounded px-1 py-0.5 mr-1 font-bold align-middle">配信停止</span>}
                          {item.name}
                        </p>
                        <p className="text-[10px] text-gray-500 truncate">{item.purposeLabel || item.type}</p>
                      </button>
                    ))}
                  </div>
                  {filtered.length > visibleCount && (
                    <button
                      onClick={() => setVisibleCount((prev) => prev + 10)}
                      className="w-full py-2 text-xs text-blue-600 font-bold hover:bg-blue-50 border-t"
                    >
                      もっと見る
                    </button>
                  )}
                </>
              )}
            </div>
          ) : (
            <button
              onClick={() => setListOpen(true)}
              className="bg-white rounded-xl shadow w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600"
            >
              &#x203A;
            </button>
          )}
        </div>

        {/* 詳細・返信 */}
        <div className="flex-1">
          {selected ? (
            <div className="bg-white rounded-xl shadow p-6">
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-lg mb-2">
                    {selected.name}
                    {isBlacklisted(selected.email) && <span className="ml-2 text-xs bg-red-600 text-white rounded px-2 py-0.5 font-bold align-middle">⛔ 配信停止中</span>}
                  </h3>
                  <p className="text-sm text-gray-500">{selected.email}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(selected.date)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => markUnread(selected.id)}
                    className="bg-gray-100 text-gray-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-200"
                  >
                    未読に戻す
                  </button>
                  {selected.email && (
                    <button
                      onClick={() => toggleBlacklistEmail(selected.email)}
                      title={isBlacklisted(selected.email) ? 'ML配信停止に登録済み（押すと解除）' : 'この人をML配信停止（ブラックリスト）に追加'}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold ${
                        isBlacklisted(selected.email) ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-white border border-red-300 text-red-600 hover:bg-red-50'
                      }`}
                    >
                      {isBlacklisted(selected.email) ? '配信停止中(BL)' : '配信停止(BL)'}
                    </button>
                  )}
                  {account === 'infoweb' && (
                    <button
                      onClick={() => toggleChatwork(selected)}
                      title={selected.chatworkOn !== false ? 'ChatWork配信OK（押すと配信しない＝除外）。押しても即配信はしません' : 'ChatWork配信しない（押すと配信OK）'}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold ${
                        selected.chatworkOn !== false
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {selected.chatworkOn !== false ? 'CW配信OK' : 'CW配信しない'}
                    </button>
                  )}
                  <button
                    onClick={() => markSpam(selected.id)}
                    className="bg-orange-50 text-orange-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-orange-100"
                  >
                    迷惑メール
                  </button>
                  <button
                    onClick={() => deleteInquiry(selected.id)}
                    className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100"
                  >
                    削除
                  </button>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm whitespace-pre-wrap">
                {selected.details}
              </div>

              {/* 返信履歴 */}
              {selected.replies && selected.replies.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-bold text-gray-600 mb-2">返信履歴</p>
                  <div className="space-y-2">
                    {selected.replies.map((reply, i) => {
                      const isCustomer = reply.from === 'customer';
                      return (
                        <div key={i} className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}>
                          <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${isCustomer ? 'bg-white border border-gray-200 rounded-tl-sm' : 'bg-blue-500 text-white rounded-tr-sm'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-bold ${isCustomer ? 'text-gray-600' : 'text-blue-100'}`}>
                                {isCustomer ? 'お客様' : 'SrWith事務局'}
                              </span>
                              <span className={`text-xs ${isCustomer ? 'text-gray-400' : 'text-blue-200'}`}>{formatDate(reply.date)}</span>
                            </div>
                            {reply.subject && <p className={`text-xs mb-1 ${isCustomer ? 'text-gray-500' : 'text-blue-200'}`}>件名: {reply.subject}</p>}
                            <p className="text-sm whitespace-pre-wrap break-words">{reply.body}</p>
                            {reply.attachments && reply.attachments.length > 0 && (
                              <div className={`mt-1.5 flex flex-wrap gap-1 ${isCustomer ? 'text-gray-500' : 'text-blue-200'}`}>
                                {reply.attachments.map((name, j) => (
                                  <span key={j} className="text-xs flex items-center gap-0.5">📎 {name}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <label className="block text-sm font-bold mb-2">返信</label>
                {accounts.length > 1 && (
                  <div className="mb-2">
                    <label className="block text-[11px] text-gray-500 mb-1">送信元</label>
                    <select value={replyFromKey} onChange={(e) => setReplyFromKey(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">このまま（{accounts.find((a) => a.key === account)?.label || account}）で送信</option>
                      {accounts.filter((a) => a.key !== account).map((a) => (
                        <option key={a.key} value={a.key}>{a.label || a.email}（{a.email}）から送信</option>
                      ))}
                    </select>
                  </div>
                )}
                {templates.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {templates.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setReplyText((prev) => prev + t.body)}
                        className="bg-gray-100 text-gray-700 px-2.5 py-1 rounded text-xs font-bold hover:bg-gray-200"
                        title={t.body}
                      >
                        {t.title}
                      </button>
                    ))}
                  </div>
                )}
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={6}
                  placeholder="返信内容を入力..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                {/* 添付ファイル */}
                <div className="mt-2">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer text-gray-500 hover:text-gray-700 text-sm">
                    <span>📎</span>
                    <span className="text-xs font-bold">ファイルを添付</span>
                    <span className="text-[10px] text-gray-400 font-normal">（30MBまで）</span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => setAttachments((prev) => [...prev, ...Array.from(e.target.files)])}
                    />
                  </label>
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {attachments.map((file, i) => (
                        <span key={i} className="inline-flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs text-gray-700">
                          📎 {file.name}
                          <button
                            onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                            className="text-red-400 hover:text-red-600 font-bold ml-0.5"
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={sendReply}
                  disabled={sending || !replyText.trim()}
                  className="mt-3 bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
                >
                  {sending ? '送信中...' : '返信を送信'}
                </button>
              </div>

              {/* 定型文管理 */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-gray-600">定型文</p>
                  <button
                    onClick={() => setShowTemplateForm(!showTemplateForm)}
                    className="text-xs text-blue-600 font-bold hover:underline"
                  >
                    {showTemplateForm ? '閉じる' : '+ 追加'}
                  </button>
                </div>
                {showTemplateForm && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-3 space-y-2">
                    <input
                      type="text"
                      value={newTemplate.title}
                      onChange={(e) => setNewTemplate((p) => ({ ...p, title: e.target.value }))}
                      placeholder="ボタン名（例: お礼）"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <textarea
                      value={newTemplate.body}
                      onChange={(e) => setNewTemplate((p) => ({ ...p, body: e.target.value }))}
                      placeholder="定型文の内容"
                      rows={3}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                    <button
                      onClick={saveTemplate}
                      className="bg-blue-600 text-white px-4 py-1.5 rounded text-xs font-bold hover:bg-blue-700"
                    >
                      保存
                    </button>
                  </div>
                )}
                {templates.length > 0 && (
                  <div className="space-y-1">
                    {templates.map((t) => (
                      <div key={t.id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-gray-700">{t.title}</p>
                          <p className="text-xs text-gray-400 truncate">{t.body}</p>
                        </div>
                        <button
                          onClick={() => deleteTemplate(t.id)}
                          className="text-red-500 text-xs font-bold hover:text-red-700 shrink-0 ml-2"
                        >
                          削除
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
              左の一覧から選択してください
            </div>
          )}
        </div>
      </div>

      {/* メールアカウント設定モーダル */}
      {showAccounts && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowAccounts(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">メールアカウント</h3>
              <button onClick={() => setShowAccounts(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>

            {accountMsg && <div className="mb-3 px-3 py-2 bg-blue-50 text-blue-700 rounded text-sm">{accountMsg}</div>}

            {/* アカウント一覧 */}
            <div className="space-y-2 mb-6">
              {accounts.map((a) => (
                <div key={a.key} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-800 truncate">{a.label || a.email}</span>
                      {a.shared ? (
                        <span className="text-[10px] bg-blue-100 text-blue-700 rounded px-1.5 py-0.5 font-bold">共有</span>
                      ) : (
                        <span className="text-[10px] bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5 font-bold">自分</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{a.email}</p>
                  </div>
                  {!a.builtin && !a.shared && (
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <button onClick={() => editAccount(a)} className="text-xs text-blue-600 font-bold hover:underline">編集</button>
                      <button onClick={() => deleteAccount(a)} className="text-xs text-red-500 font-bold hover:underline">削除</button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* 追加・編集フォーム */}
            <div className="border-t pt-4">
              <p className="text-sm font-bold text-gray-700 mb-3">{accountForm.key ? 'アカウントを編集' : '自分のアカウントを追加'}</p>
              <div className="grid grid-cols-2 gap-3">
                <label className="col-span-2 text-xs font-bold text-gray-600">
                  種類
                  <select
                    value={accountForm.provider}
                    onChange={(e) => pickProvider(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.entries(MAIL_PROVIDERS).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </label>
                <label className="col-span-2 text-xs font-bold text-gray-600">
                  メールアドレス
                  <input type="email" value={accountForm.email}
                    onChange={(e) => setAccountForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="you@example.com"
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </label>
                <label className="col-span-2 text-xs font-bold text-gray-600">
                  パスワード{accountForm.key && <span className="font-normal text-gray-400">（変更する場合のみ入力）</span>}
                  {accountForm.provider === 'gmail' && <span className="font-normal text-orange-500">　※Gmailはアプリパスワード</span>}
                  <input type="password" value={accountForm.pass}
                    onChange={(e) => setAccountForm((f) => ({ ...f, pass: e.target.value }))}
                    placeholder={accountForm.key ? '（未変更なら空欄）' : ''}
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </label>
                <label className="text-xs font-bold text-gray-600">
                  表示名（タブ名）
                  <input type="text" value={accountForm.label}
                    onChange={(e) => setAccountForm((f) => ({ ...f, label: e.target.value }))}
                    placeholder="例: 自分"
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </label>
                <label className="text-xs font-bold text-gray-600">
                  ログインユーザー（省略可）
                  <input type="text" value={accountForm.user}
                    onChange={(e) => setAccountForm((f) => ({ ...f, user: e.target.value }))}
                    placeholder="通常はメールアドレスと同じ"
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </label>
                <label className="text-xs font-bold text-gray-600">
                  IMAPホスト
                  <input type="text" value={accountForm.imapHost}
                    onChange={(e) => setAccountForm((f) => ({ ...f, imapHost: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </label>
                <label className="text-xs font-bold text-gray-600">
                  IMAPポート
                  <input type="number" value={accountForm.imapPort}
                    onChange={(e) => setAccountForm((f) => ({ ...f, imapPort: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </label>
                <label className="text-xs font-bold text-gray-600">
                  SMTPホスト
                  <input type="text" value={accountForm.smtpHost}
                    onChange={(e) => setAccountForm((f) => ({ ...f, smtpHost: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </label>
                <label className="text-xs font-bold text-gray-600">
                  SMTPポート
                  <input type="number" value={accountForm.smtpPort}
                    onChange={(e) => setAccountForm((f) => ({ ...f, smtpPort: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </label>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button onClick={saveAccount} disabled={savingAccount}
                  className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                  {savingAccount ? '保存中...' : (accountForm.key ? '更新' : '追加')}
                </button>
                {accountForm.key && (
                  <button onClick={() => { setAccountForm(emptyAccountForm()); setAccountMsg(''); }}
                    className="text-sm text-gray-500 font-bold hover:underline">
                    新規追加に戻す
                  </button>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-3">
                ※ここで登録したアカウントはあなた専用（他の管理者には表示されません）。パスワードはサーバーに保存され、画面には表示されません。
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailAdmin;
